<template>
  <div class="initialization-sequence">
    <div class="sequence-content">
      <h1 class="sequence-title"><span class="title-icon">🦋</span> The Ephemera Archive</h1>

      <!-- Phase 1: Opening doors -->
      <p v-if="phase === 'loading'" class="sequence-subtitle">
        Opening archive doors...
      </p>

      <!-- Phase 2: Metadata reveal -->
      <template v-if="phase === 'reveal'">
        <p class="sequence-subtitle">Cataloguing your visit...</p>
        <div class="metadata-reveal">
          <TransitionGroup name="reveal-item" tag="div" class="reveal-list">
            <div
              v-for="(item, index) in revealedItems"
              :key="item.label"
              class="reveal-row"
            >
              <span class="reveal-icon">{{ item.icon }}</span>
              <span class="reveal-label">{{ item.label }}:</span>
              <span class="reveal-value">{{ item.value }}</span>
              <span
                v-if="item.isNew"
                class="new-badge"
              >
                NEW SPECIMEN
              </span>
            </div>
          </TransitionGroup>
        </div>
      </template>

      <!-- Phase 3: Summary -->
      <template v-if="phase === 'summary'">
        <div class="summary-section">
          <template v-if="newDiscoveries > 0">
            <p class="summary-headline">
              You discovered {{ newDiscoveries }} new specimen{{ newDiscoveries !== 1 ? 's' : '' }}!
            </p>
          </template>
          <template v-else>
            <p class="summary-headline">
              Welcome back, Curator {{ curatorId }}
            </p>
          </template>
          <p class="summary-sub">Entering the archive...</p>
        </div>
      </template>

      <div v-if="errorMsg" class="error-message">
        {{ errorMsg }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useArchiveStore } from '../stores/archive';

const emit = defineEmits<{
  complete: [];
}>();

const archiveStore = useArchiveStore();

const phase = ref<'loading' | 'reveal' | 'summary'>('loading');
const revealedItems = ref<Array<{ label: string; value: string; icon: string; isNew: boolean }>>([]);
const errorMsg = ref<string | null>(null);

const curatorId = computed(() => archiveStore.sessionId?.slice(0, 8) || '???');
const newDiscoveries = computed(() => archiveStore.contributionSummary?.newDiscoveries || 0);

function getMetadataItems(): Array<{ label: string; value: string; icon: string; specimenType?: string }> {
  const md = archiveStore.visitorMetadata;
  if (!md) return [];

  const items: Array<{ label: string; value: string; icon: string; specimenType?: string }> = [];

  if (md.country) {
    const flag = getCountryFlag(md.country);
    items.push({ label: 'Country', value: `${flag} ${md.country}`, icon: '🌍', specimenType: 'country' });
  }
  if (md.browser) {
    items.push({ label: 'Browser', value: md.browser, icon: '🌐', specimenType: 'browser' });
  }
  if (md.os) {
    items.push({ label: 'OS', value: md.os, icon: '💻', specimenType: 'os' });
  }
  if (md.deviceType) {
    items.push({ label: 'Device', value: md.deviceType, icon: md.deviceType === 'mobile' ? '📱' : '🖥️', specimenType: 'deviceType' });
  }
  if (md.timezone) {
    items.push({ label: 'Timezone', value: md.timezone, icon: '🕐', specimenType: 'timezone' });
  }
  if (md.season) {
    items.push({ label: 'Season', value: md.season.charAt(0).toUpperCase() + md.season.slice(1), icon: '🍂', specimenType: 'season' });
  }

  return items;
}

function getCountryFlag(code: string): string {
  try {
    const codePoints = code.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  } catch {
    return '🏳️';
  }
}

function isNewSpecimen(specimenType: string, value: string): boolean {
  return archiveStore.specimenResults.some(
    s => s.type === specimenType && s.isNew
  );
}

onMounted(async () => {
  // Wait for the store to initialize (visit is recorded during initialize)
  const checkReady = () => {
    return archiveStore.visitorMetadata !== null || archiveStore.error !== null;
  };

  // Poll for readiness (store.initialize() is called from App.vue onMounted)
  let waited = 0;
  while (!checkReady() && waited < 15000) {
    await new Promise(r => setTimeout(r, 100));
    waited += 100;
  }

  if (archiveStore.error) {
    errorMsg.value = archiveStore.error;
    // Still complete after a delay so user can see error
    await new Promise(r => setTimeout(r, 2000));
    emit('complete');
    return;
  }

  // Phase 2: Reveal metadata line by line
  phase.value = 'reveal';
  const items = getMetadataItems();

  for (const item of items) {
    await new Promise(r => setTimeout(r, 300));
    revealedItems.value.push({
      ...item,
      isNew: item.specimenType ? isNewSpecimen(item.specimenType, item.value) : false,
    });
  }

  // Wait a beat after last item
  await new Promise(r => setTimeout(r, 600));

  // Phase 3: Summary
  phase.value = 'summary';
  await new Promise(r => setTimeout(r, 1500));

  emit('complete');
});
</script>

<style scoped>
.initialization-sequence {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-sepia-light) 0%, var(--color-sepia-medium) 100%);
}

.sequence-content {
  text-align: center;
  padding: var(--spacing-2xl);
  max-width: 600px;
}

.sequence-title {
  font-size: 3rem;
  margin-bottom: var(--spacing-sm);
  color: var(--color-black-primary);
  font-family: var(--font-serif);
}

.sequence-subtitle {
  font-size: 1.25rem;
  color: var(--color-burgundy);
  margin-bottom: var(--spacing-2xl);
  font-style: italic;
}

.metadata-reveal {
  margin-top: var(--spacing-xl);
  text-align: left;
}

.reveal-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.reveal-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 6px;
  font-size: 1rem;
}

.reveal-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.reveal-label {
  font-weight: 600;
  color: var(--color-black-secondary);
  min-width: 80px;
}

.reveal-value {
  color: var(--color-black-primary);
  font-weight: 500;
}

.new-badge {
  margin-left: auto;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 2px 8px;
  background-color: var(--color-legendary);
  color: var(--color-black-primary);
  border-radius: 4px;
  animation: pulse-badge 1.5s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.reveal-item-enter-active {
  transition: all 0.4s ease;
}

.reveal-item-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.summary-section {
  margin-top: var(--spacing-2xl);
}

.summary-headline {
  font-family: var(--font-serif);
  font-size: 1.75rem;
  color: var(--color-black-primary);
  margin-bottom: var(--spacing-md);
}

.summary-sub {
  font-size: 1rem;
  color: var(--color-burgundy);
  font-style: italic;
  animation: fade-pulse 1.5s ease-in-out infinite;
}

@keyframes fade-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.error-message {
  margin-top: var(--spacing-xl);
  padding: var(--spacing-md);
  background-color: #fee;
  color: #c00;
  border-radius: 4px;
}
</style>
